// components/Admin/MWLWorklistStatus.jsx
// Shows current DICOM Modality Worklist (.wl) files and lets an admin force a resync.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, CircularProgress, Chip, Alert, Snackbar,
} from '@mui/material';
import { Sync as SyncIcon, Description as FileIcon } from '@mui/icons-material';
import { mwlService } from '../../services/hl7';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function MWLWorklistStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mwlService.getStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load MWL status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await mwlService.syncNow();
      setSnackbar({ open: true, message: `Resync complete — ${data.activeEntries} active entries`, severity: 'success' });
      fetchStatus();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Resync failed', severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>🖥️ DICOM Worklist (MWL) Status</Typography>
        <Button variant="contained" startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          onClick={handleSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Resync Now'}
        </Button>
      </Box>

      {status && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip label={`${status.count} active worklist entries`} color="primary" />
          <Chip label={status.directory} variant="outlined" sx={{ fontFamily: 'monospace' }} />
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Order ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Worklist File</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : !status || status.files.length === 0 ? (
              <TableRow><TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                <FileIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No active worklist entries — no scheduled orders need a scanner pull right now.</Typography>
              </TableCell></TableRow>
            ) : status.files.map(f => (
              <TableRow key={f.name} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{f.orderId}</TableCell>
                <TableCell>{f.name}</TableCell>
                <TableCell>{formatBytes(f.sizeBytes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
